import React, { useRef, useState } from 'react';
import { Box, Grid, Card, CardMedia } from '@mui/material';
import { Delete as DeleteIcon, CloudUpload as CloudUploadIcon } from '@mui/icons-material';
import instanceAxios from '@/util/axios';
import { publicBusinessAdsUploadUrl } from '@/constants';
import { Button, Input, IconButton, Typography } from '@sm-dashboard/ui-components';

interface DescriptionAndMediaFormProps {
  formData: {
    shortDescription: string;
    fullDescription: string;
    mainImage: string;
    additionalMedia: string[];
    websiteLink: string;
    facebookLink: string;
    instagramLink: string;
    recommendations: string;
  };
  setFormData: React.Dispatch<React.SetStateAction<any>>;
}

const DescriptionAndMediaForm: React.FC<DescriptionAndMediaFormProps> = ({ formData, setFormData }) => {
  const mainImageInputRef = useRef<HTMLInputElement>(null);
  const additionalMediaInputRef = useRef<HTMLInputElement>(null);
  const [uploadingMain, setUploadingMain] = useState(false);
  const [uploadingAdditional, setUploadingAdditional] = useState(false);

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData((prev: any) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>, field: 'mainImage' | 'additionalMedia') => {
    const file = e.target.files?.[0];
    if (!file) return;

    const setUploading = field === 'mainImage' ? setUploadingMain : setUploadingAdditional;
    setUploading(true);

    const uploadFormData = new FormData();
    uploadFormData.append('file', file);

    try {
      const response = await instanceAxios.post(publicBusinessAdsUploadUrl, uploadFormData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      const { url } = response.data;

      if (field === 'mainImage') {
        setFormData((prev: any) => ({ ...prev, mainImage: url }));
      } else {
        setFormData((prev: any) => ({
          ...prev,
          additionalMedia: [...prev.additionalMedia, url]
        }));
      }
    } catch (error) {
      console.error('File upload failed:', error);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleRemoveFile = (index: number, field: 'mainImage' | 'additionalMedia') => {
    if (field === 'mainImage') {
      setFormData((prev: any) => ({ ...prev, mainImage: '' }));
    } else {
      const newMedia = [...formData.additionalMedia];
      newMedia.splice(index, 1);
      setFormData((prev: any) => ({ ...prev, additionalMedia: newMedia }));
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="h6">תיאור העסק ומדיה</Typography>
      <Input
        label="תיאור קצר (עד 70 תווים)"
        name="shortDescription"
        value={formData.shortDescription}
        onChange={handleTextChange}
        inputProps={{ maxLength: 70 }}
        required
      />
      <Input
        label="תיאור מפורט (אופציונלי)"
        name="fullDescription"
        value={formData.fullDescription}
        onChange={handleTextChange}
        multiline
        rows={4}
        placeholder="כאן תוכלו לפרט על העסק, השירותים המוצעים, ניסיון, המלצות ועוד..."
      />

      <Typography variant="h6">קישורים</Typography>
      <Input label="קישור לאתר" name="websiteLink" value={formData.websiteLink} onChange={handleTextChange} />
      <Input label="קישור לפייסבוק" name="facebookLink" value={formData.facebookLink} onChange={handleTextChange} />
      <Input label="קישור לאינסטגרם" name="instagramLink" value={formData.instagramLink} onChange={handleTextChange} />

      <Typography variant="h6">העלאת מדיה</Typography>
      <Box sx={{ fontStyle: 'italic', color: 'text.secondary', fontSize: '0.875rem' }}>
        <span role="img" aria-label="tip">
          💡
        </span>{' '}
        טיפ: בחרו תמונה ראשית ללא טקסט עליה, שתשדר בצורה מקצועית את העסק/הפעילות שלכם. תמונה זו תופיע בפיד הראשי.
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Button
            variant="outlined"
            startIcon={<CloudUploadIcon />}
            fullWidth
            loading={uploadingMain}
            onClick={() => mainImageInputRef.current?.click()}
          >
            העלאת תמונה ראשית
          </Button>
          <input
            ref={mainImageInputRef}
            type="file"
            hidden
            accept="image/*"
            onChange={e => handleFileUpload(e, 'mainImage')}
          />
          {formData.mainImage && (
            <Box sx={{ mt: 2, position: 'relative' }}>
              <Card>
                <CardMedia component="img" image={formData.mainImage} sx={{ maxHeight: 200, objectFit: 'contain' }} />
              </Card>
              <Box sx={{ position: 'absolute', top: 0, right: 0 }}>
                <IconButton
                  shape="circle"
                  color="error"
                  size="small"
                  aria-label="מחק תמונה ראשית"
                  onClick={() => handleRemoveFile(0, 'mainImage')}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Box>
          )}
        </Grid>
        <Grid item xs={12} sm={6}>
          <Button
            variant="outlined"
            startIcon={<CloudUploadIcon />}
            fullWidth
            disabled={formData.additionalMedia.length >= 5}
            loading={uploadingAdditional}
            onClick={() => additionalMediaInputRef.current?.click()}
          >
            העלאת מדיה נוספת ({formData.additionalMedia.length}/5)
          </Button>
          <input
            ref={additionalMediaInputRef}
            type="file"
            hidden
            accept="image/*,video/*"
            onChange={e => handleFileUpload(e, 'additionalMedia')}
            disabled={formData.additionalMedia.length >= 5}
          />
          {formData.additionalMedia.length >= 5 && (
            <Box sx={{ display: 'block', mt: 1 }}>
              <Typography variant="caption" color="text.secondary">
                הגעת למקסימום 5 קבצים
              </Typography>
            </Box>
          )}
          <Grid container spacing={1} sx={{ mt: 1 }}>
            {formData.additionalMedia.map((url, index) => (
              <Grid item key={index} xs={6} sm={4}>
                <Box sx={{ position: 'relative' }}>
                  <Card>
                    <CardMedia
                      component={url.startsWith('data:video') ? 'video' : 'img'}
                      src={url}
                      sx={{ height: 100, objectFit: 'cover' }}
                      controls={url.startsWith('data:video')}
                    />
                  </Card>
                  <Box sx={{ position: 'absolute', top: 0, right: 0 }}>
                    <IconButton
                      shape="circle"
                      color="error"
                      size="small"
                      aria-label="מחק מדיה"
                      onClick={() => handleRemoveFile(index, 'additionalMedia')}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Grid>

      <Typography variant="h6">המלצות</Typography>
      <Input
        label="המלצות (טקסט חופשי)"
        name="recommendations"
        value={formData.recommendations}
        onChange={handleTextChange}
        multiline
        rows={3}
        inputProps={{ maxLength: 700 }}
        helperText={`${formData.recommendations.length}/700 תווים`}
      />
    </Box>
  );
};

export default DescriptionAndMediaForm;
